<template>
  <div class="payment-page">
    <header class="payment-page__header">
      <h1 class="payment-page__title">{{ $t('deposit.deposit_form') }}</h1>
      <p class="payment-page__subtitle">
        {{ $t('deposit.subtitle') }}
      </p>
    </header>

    <section class="payment-panel">
      <div class="payment-panel__accent"></div>
      <div class="payment-panel__body">
        <form @submit.prevent>
          <div class="payment-field">
            <label class="payment-field__label">{{ $t('deposit.choose_payment_type') }}</label>
            <AccountSelect
              :data="selected_account"
              field="account_id"
              :accounts="accounts"
            />
            <span class="payment-field__error">{{ errors.account }}</span>
          </div>

          <div v-if="account.length !== 0" class="payment-account-card">
            <span class="payment-account-card__type">{{ account[0].name }}</span>
            <span class="payment-account-card__name">{{ account[0].account_name }}</span>
            <h3 class="payment-account-card__number" id="textToCopy">{{ account[0].number }}</h3>
            <button type="button" class="payment-account-card__copy" @click="copyText">
              <i class="fas fa-copy" aria-hidden="true"></i>
              {{ $t('deposit.copy') }}
            </button>
            <span class="payment-account-card__note">
              <strong>****</strong> {{ account[0].note }}
            </span>
          </div>

          <div class="payment-field">
            <label class="payment-field__label">{{ $t('deposit.amount') }}</label>
            <span class="payment-field__hint">{{ $t('deposit.minimum_deposit') }}</span>
            <input
              type="number"
              class="payment-input"
              v-model="depositData.amount"
            />
            <span class="payment-field__error">{{ errors.amount }}</span>
          </div>

          <div class="payment-amount-grid">
            <span v-show="false">{{ testing }}</span>
            <button
              v-for="amount in quickAmounts"
              :key="amount"
              type="button"
              class="payment-amount-chip"
              :class="{ 'payment-amount-chip--active': depositData.amount === amount }"
              @click="depositCustomAmount(amount)"
            >
              {{ amount.toLocaleString() }} MMK
            </button>
          </div>

          <div class="payment-field">
            <label class="payment-field__label">
              {{ $t('deposit.last_six_transaction_id') }}
            </label>
            <span class="payment-field__hint">
              {{ $t('deposit.last_six_transaction_id_hint') }}
            </span>
            <input
              type="text"
              class="payment-input"
              v-model="depositData.last_six_transaction_id"
              maxlength="6"
              inputmode="numeric"
              pattern="[0-9]*"
              @input="onLastSixInput"
            />
            <span class="payment-field__error">{{ errors.last_six_transaction_id }}</span>
          </div>

          <div class="payment-field">
            <label class="payment-field__label">
              {{ $t('deposit.screenshot') }}
            </label>
            <span class="payment-field__hint">
              {{ $t('deposit.screenshot_hint') }}
            </span>
            <div class="payment-upload">
              <van-uploader
                v-model="imageUploader"
                :max-count="1"
              />
            </div>
            <span class="payment-field__error">{{ errors.image }}</span>
          </div>

          <div class="payment-field">
            <label class="payment-field__label">{{ $t('voucher.remark') }}</label>
            <textarea
              class="payment-input"
              v-model="depositData.remark"
              rows="3"
            />
            <span class="payment-field__error">{{ errors.remark }}</span>
          </div>

          <van-radio-group
            v-if="is_promotion && $store.state.disableJson?.promotions?.is_open"
            v-model="depositData.is_bonus"
            class="payment-bonus-group"
          >
            <van-radio :name="false" checked-color="#CFA757">
              {{ $t('deposit.no_bonus') }}
            </van-radio>
            <van-radio :name="true" checked-color="#CFA757">
              {{ $t('deposit.claim_bonus') }}
            </van-radio>
          </van-radio-group>

          <button
            type="button"
            class="payment-submit-btn"
            :disabled="isConfirm"
            @click="sendDepositData"
          >
            <Loading
              v-if="isConfirm"
              color="#1a1a1a"
              size="24"
            />
            <span v-else>{{ $t('deposit.submit') }}</span>
          </button>
        </form>
      </div>
    </section>

    <div class="page-bottom-spacer" aria-hidden="true"></div>
  </div>
</template>
<script>
import { Toast, Dialog, Loading } from 'vant';
import { mapActions } from 'vuex';
import AccountSelect from './components/AccountSelect.vue';

export default {
  components: { AccountSelect, Loading },
  data() {
    return {
      selected_account: {},
      accounts: [],
      account: [],
      imageUploader: [],
      depositData: {
        is_bonus: false
      },
      is_promotion: true,
      errors: {},
      isConfirm: false,
      testing: 0,
      quickAmounts: [2000, 5000, 10000, 30000, 50000, 100000],
    };
  },
  methods: {
    ...mapActions(['fetchUser']),
    depositCustomAmount(number) {
      this.depositData.amount = number;
      this.testing = number;
    },
    onLastSixInput(event) {
      const digitsOnly = String(event.target.value || '').replace(/\D/g, '').slice(0, 6);
      this.depositData.last_six_transaction_id = digitsOnly;
    },
    getApiErrorMessage(error) {
      const fallbackMessage = this.$t('deposit.submit_fail');

      if (!error) {
        return fallbackMessage;
      }

      if (!error.response) {
        return error.message || fallbackMessage;
      }

      const { data, status, statusText } = error.response;

      if (!data) {
        return statusText ? `${status} ${statusText}` : fallbackMessage;
      }

      if (typeof data === 'string') {
        const trimmed = data.trim();
        return trimmed || fallbackMessage;
      }

      if (data.errors && typeof data.errors === 'object') {
        const messages = Object.values(data.errors)
          .flat()
          .filter(Boolean);
        if (messages.length > 0) {
          return messages.join('\n');
        }
      }

      if (data.message) {
        return data.message;
      }

      if (typeof data.error === 'string' && data.error) {
        return data.error;
      }

      return statusText ? `${status} ${statusText}` : fallbackMessage;
    },
    copyText() {
      const textToCopy = document.getElementById('textToCopy').innerText;
      const textarea = document.createElement('textarea');
      textarea.value = textToCopy;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      Toast.success(this.$t('deposit.copied'));
    },
    async fetchAccounts() {
      try {
        const res = await axios.get('/accounts', {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        this.accounts = res.data.data;
        this.is_promotion = res.data.is_promotion;
      } catch (error) {
        console.log(error);
      }
    },
    async sendDepositData() {
      try {
        const authUser = await this.fetchUser();
        this.errors = {};
        if (!this.selected_account.account_id) {
          this.errors.account = this.$t('deposit.enter_account_type');
          return false;
        }

        const lastSix = String(this.depositData.last_six_transaction_id || '').trim();
        const hasImage = this.imageUploader.length > 0;
        if (!lastSix && !hasImage) {
          const eitherOrMessage = this.$t('deposit.enter_either_proof');
          this.errors.last_six_transaction_id = eitherOrMessage;
          this.errors.image = eitherOrMessage;
          return false;
        }
        if (lastSix && !/^\d{6}$/.test(lastSix)) {
          this.errors.last_six_transaction_id = this.$t('deposit.enter_last_six_digits');
          return false;
        }

        if (!this.depositData.amount) {
          this.errors.amount = this.$t('deposit.enter_amount');
          return false;
        }
        if (this.depositData.amount < 2000) {
          this.errors.amount = this.$t('deposit.enter_at_least');
          return false;
        }
        this.isConfirm = true;
        if (this.imageUploader[0]) {
          this.depositData.transaction_image = this.imageUploader[0].content;
        } else {
          delete this.depositData.transaction_image;
        }
        if (lastSix) {
          this.depositData.last_six_transaction_id = lastSix;
        } else {
          delete this.depositData.last_six_transaction_id;
        }
        this.depositData.account_id = this.account[0].id;
        this.depositData.date = this.currentTime;
        await axios.post('/deposits', this.depositData, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        this.imageUploader = [];
        this.depositData = { is_bonus: false };
        this.selected_account.account_id = null;
        Toast.success(this.$t('deposit.success'));
        this.isConfirm = false;
      } catch (error) {
        this.isConfirm = false;
        Dialog.alert({
          message: this.getApiErrorMessage(error),
          confirmButtonText: this.$t('deposit.go_back'),
        });
      }
      return true;
    },
    createDateWithTimeZone() {
      const moment = require('moment-timezone');
      const dateInTimeZone = moment.tz(new Date(), 'Asia/Rangoon');
      const year = dateInTimeZone.year();
      const month = dateInTimeZone.month();
      const day = dateInTimeZone.date();
      const hour = dateInTimeZone.hour();
      const minute = dateInTimeZone.minute();
      const second = dateInTimeZone.second();
      const millisecond = dateInTimeZone.millisecond();
      return new Date(year, month, day, hour, minute, second, millisecond);
    },
  },
  watch: {
    'selected_account.account_id': function () {
      this.account = this.accounts.filter(
        (account) => account.id === this.selected_account.account_id,
      );
    },
  },
  computed: {
    currentTime() {
      return moment(this.createDateWithTimeZone()).format('lll');
    },
  },
  async mounted() {
    this.fetchAccounts();
    const authUser = await this.fetchUser();
    if (!authUser) {
      this.$router.push('/auth/login');
    }
    if (authUser && !authUser.is_verified) {
      this.$router.push('/auth/verify');
    }
  },
};
</script>

<style scoped>
.payment-panel {
  margin-bottom: 1.25rem;
}

.payment-panel__body {
  padding-bottom: 1.5rem;
}

.payment-submit-btn {
  margin-top: 0.25rem;
  margin-bottom: 1.5rem;
}
</style>
